import {testIdProps} from '@github-ui/test-id-props'
import {CopilotIcon, DeviceDesktopIcon, InfoIcon, PencilIcon, TriangleDownIcon} from '@primer/octicons-react'
import {Button, FormControl, Stack, Textarea} from '@primer/react'
import {useCallback} from 'react'
import invariant from 'tiny-invariant'

import type {PersistedOption} from '../../../api/columns/contracts/single-select'
import {MemexActionType, type MemexWorkflowAction} from '../../../api/workflows/contracts'
import {useWorkflowSetFieldAction} from '../../../hooks/use-workflow-action'
import {useAutomationGraph} from '../../../state-providers/workflows/use-automation-graph'
import {WorkflowResources} from '../../../strings'
import {Connector} from '../connector'
import {StatusPicker} from '../status-picker'
import styles from './assign-copilot-block.module.css'
import {AutomationBlock} from './automation-block'

const MAX_INSTRUCTIONS_LENGTH = 4000
// Output mode is hardcoded for the MVP — only "create a pull request" is supported.
const DEFAULT_OUTPUT_MODE = 'pull_request'

export const AssignCopilotBlock = () => {
  const {isEditing, workflow, setWorkflow} = useAutomationGraph()

  const getProjectItemsAction = workflow.actions.find(a => a.actionType === MemexActionType.GetProjectItems)
  const assignCopilotAction = workflow.actions.find(a => a.actionType === MemexActionType.AssignCopilot)
  invariant(assignCopilotAction, 'AssignCopilot action must be defined')
  invariant(getProjectItemsAction, 'GetProjectItems action must be defined')

  const {column, selectedOption: sourceOption} = useWorkflowSetFieldAction(getProjectItemsAction)
  const statusOptions = column && 'options' in column.settings ? column.settings.options : []

  // Update an action in-place by actionType. We use setWorkflow + actionType
  // matching here (instead of the shared replaceAction helper, which matches by
  // action.id) because Auto-assign-agent's default workflow has two actions and unsaved
  // default actions all have action.id === undefined — id-based matching would
  // conflate them.
  const updateActionByType = useCallback(
    (actionType: MemexActionType, updater: (a: MemexWorkflowAction) => MemexWorkflowAction) => {
      setWorkflow(prev => {
        const actions = prev.actions.map(a => (a.actionType === actionType ? updater(a) : a))
        return {...prev, actions}
      })
    },
    [setWorkflow],
  )

  const onSourceColumnSelected = (option: PersistedOption | undefined) => {
    updateActionByType(MemexActionType.GetProjectItems, a => ({
      ...a,
      arguments: {...a.arguments, fieldOptionId: option?.id},
    }))
  }

  const onInstructionsChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value
    updateActionByType(MemexActionType.AssignCopilot, a => ({
      ...a,
      arguments: {
        ...a.arguments,
        instruction: value,
        outputMode: DEFAULT_OUTPUT_MODE,
      },
    }))
  }

  const instructions = assignCopilotAction.arguments.instruction ?? ''

  return (
    <>
      <AutomationBlock
        icon={PencilIcon}
        iconBg="var(--bgColor-attention-muted)"
        iconColor="var(--fgColor-attention)"
        headerDescription={WorkflowResources.autoAssignAgentStatusUpdatedLabel}
        {...testIdProps('automation-assign-copilot-block')}
      >
        <StatusPicker
          onStatusSelected={onSourceColumnSelected}
          options={statusOptions}
          selectedOption={sourceOption ? {...sourceOption} : undefined}
          isEditing={isEditing}
          headerDescription={WorkflowResources.assignCopilotSourceColumnLabel}
          testId="workflow-auto-assign-agent-source-column"
        />
      </AutomationBlock>

      <Connector />

      <AutomationBlock
        icon={CopilotIcon}
        iconBg="var(--bgColor-success-muted)"
        iconColor="var(--fgColor-success)"
        headerDescription={WorkflowResources.assignCopilotAssignAgentHeading}
        headerEnd={
          <Stack direction="horizontal" align="center" gap="condensed" wrap="wrap" className={styles.assignAgentInfo}>
            <InfoIcon size={14} />
            <span>{WorkflowResources.assignCopilotAssignAgentInfo}</span>
          </Stack>
        }
      >
        <Stack
          direction="horizontal"
          align="center"
          justify="space-between"
          gap="normal"
          wrap="wrap"
          className={styles.formRow}
        >
          <Stack.Item grow>
            <FormControl disabled>
              <FormControl.Label>{WorkflowResources.assignCopilotAgentLabel}</FormControl.Label>
              <FormControl.Caption>{WorkflowResources.assignCopilotAgentCaption}</FormControl.Caption>
            </FormControl>
          </Stack.Item>
          <Button
            leadingVisual={CopilotIcon}
            trailingVisual={TriangleDownIcon}
            disabled
            size="small"
            {...testIdProps('workflow-auto-assign-agent-agent-selector')}
          >
            {WorkflowResources.assignCopilotAgentCopilot}
          </Button>
        </Stack>

        <Stack
          direction="horizontal"
          align="center"
          justify="space-between"
          gap="normal"
          wrap="wrap"
          className={styles.formRow}
        >
          <Stack.Item grow>
            <FormControl disabled>
              <FormControl.Label>{WorkflowResources.assignCopilotModelLabel}</FormControl.Label>
              <FormControl.Caption>{WorkflowResources.assignCopilotModelCaption}</FormControl.Caption>
            </FormControl>
          </Stack.Item>
          <Button
            disabled
            trailingVisual={TriangleDownIcon}
            size="small"
            {...testIdProps('workflow-auto-assign-agent-model')}
          >
            {WorkflowResources.assignCopilotModelAutoLabel}
          </Button>
        </Stack>

        <Stack
          direction="horizontal"
          align="center"
          justify="space-between"
          gap="normal"
          wrap="wrap"
          className={styles.formRow}
        >
          <Stack.Item grow>
            <FormControl disabled>
              <FormControl.Label>{WorkflowResources.assignCopilotRepositoryLabel}</FormControl.Label>
              <FormControl.Caption>{WorkflowResources.assignCopilotRepositoryCaption}</FormControl.Caption>
            </FormControl>
          </Stack.Item>
          <Button
            leadingVisual={DeviceDesktopIcon}
            trailingVisual={TriangleDownIcon}
            disabled
            size="small"
            {...testIdProps('workflow-auto-assign-agent-repository')}
          >
            {WorkflowResources.assignCopilotRepositorySetByIssueLabel}
          </Button>
        </Stack>

        <div className={styles.formRow}>
          <FormControl>
            <FormControl.Label>{WorkflowResources.assignCopilotInstructionsLabel}</FormControl.Label>
            <FormControl.Caption>{WorkflowResources.assignCopilotInstructionsCaption}</FormControl.Caption>
            <Textarea
              value={instructions}
              onChange={onInstructionsChange}
              placeholder={WorkflowResources.assignCopilotInstructionsPlaceholder}
              maxLength={MAX_INSTRUCTIONS_LENGTH}
              disabled={!isEditing}
              className={styles.instructionsTextarea}
              resize="vertical"
              rows={5}
              aria-label={WorkflowResources.assignCopilotInstructionsLabel}
              {...testIdProps('workflow-auto-assign-agent-instructions')}
            />
          </FormControl>
        </div>
      </AutomationBlock>
    </>
  )
}
